import React from 'react'
import { useLanguage } from '../../hooks/useLanguage'
import { useAuth } from '../../hooks/useAuth'
import { 
  RoadIcon, 
  GarbageIcon, 
  WaterIcon, 
  StreetlightIcon, 
  ElectricityIcon,
  ChevronRightIcon 
} from '../icons/Icons'

// Ensure this file is treated as a module
export {}

interface QuickActionCard {
  icon: React.ComponentType<{ className?: string }>
  titleKey: string;
  descKey: string;
  category: string;
  color: string;
  bgColor: string;
}

const HeroSection: React.FC = () => {
  const { t } = useLanguage()
  const { isAuthenticated } = useAuth()

  const quickActions: QuickActionCard[] = [
    {
      icon: RoadIcon,
      titleKey: 'potholeTitle',
      descKey: 'potholeDesc',
      category: 'road',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50 hover:bg-orange-100'
    },
    {
      icon: GarbageIcon,
      titleKey: 'garbageTitle',
      descKey: 'garbageDesc',
      category: 'garbage',
      color: 'text-green-600',
      bgColor: 'bg-green-50 hover:bg-green-100'
    },
    {
      icon: StreetlightIcon,
      titleKey: 'streetlightTitle',
      descKey: 'streetlightDesc',
      category: 'streetlight',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50 hover:bg-yellow-100'
    },
    {
      icon: WaterIcon,
      titleKey: 'waterTitle',
      descKey: 'waterDesc',
      category: 'water',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50 hover:bg-blue-100'
    },
    {
      icon: ElectricityIcon,
      titleKey: 'otherTitle',
      descKey: 'otherDesc',
      category: 'other',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50 hover:bg-purple-100'
    }
  ]

  const handleQuickAction = (category: string) => {
    // This would open the complaint form with pre-selected category
    console.log('Quick action for category:', category)
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 via-gray-50 to-blue-50">
      
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        <div className="text-center">
          {/* Hero Content */}
          <div className="max-w-4xl mx-auto mb-8 sm:mb-12">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
              {t('heroTitle')}
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed px-2">
              {t('heroSubtitle')}
            </p>
            
            {/* Main Action Buttons - Improved mobile responsiveness */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-6 sm:mb-8">
              <button className="w-full sm:w-auto px-6 py-3 sm:px-8 sm:py-4 bg-blue-600 text-white text-base sm:text-lg font-semibold rounded-xl hover:bg-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                {t('reportIssue')}
              </button>
              {isAuthenticated && (
                <button className="w-full sm:w-auto px-6 py-3 sm:px-8 sm:py-4 bg-white text-blue-600 text-base sm:text-lg font-semibold rounded-xl border-2 border-blue-600 hover:bg-blue-50 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                  {t('viewComplaints')}
                </button>
              )}
              <button className="w-full sm:w-auto px-6 py-3 sm:px-8 sm:py-4 bg-gray-100 text-gray-700 text-base sm:text-lg font-semibold rounded-xl hover:bg-gray-200 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                {t('publicDashboard')}
              </button>
            </div>
          </div>

          {/* Statistics Cards - Improved mobile responsiveness */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12">
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg border border-gray-100">
              <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-1.5 sm:mb-2">1,247</div>
              <div className="text-gray-600 font-medium text-sm sm:text-base">Issues Resolved</div>
              <div className="text-xs sm:text-sm text-green-600 mt-1">↗ +12% this month</div>
            </div>
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg border border-gray-100">
              <div className="text-2xl sm:text-3xl font-bold text-orange-600 mb-1.5 sm:mb-2">324</div>
              <div className="text-gray-600 font-medium text-sm sm:text-base">Active Reports</div>
              <div className="text-xs sm:text-sm text-blue-600 mt-1">In Progress</div>
            </div>
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg border border-gray-100">
              <div className="text-2xl sm:text-3xl font-bold text-green-600 mb-1.5 sm:mb-2">89%</div>
              <div className="text-gray-600 font-medium text-sm sm:text-base">Resolution Rate</div>
              <div className="text-xs sm:text-sm text-green-600 mt-1">↗ +5% this month</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Section - Improved mobile responsiveness */}
      <div className="bg-white py-8 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
              {t('quickActions')}
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-2">
              Select a category to quickly report common civic issues in your area
            </p>
          </div>

          {/* Quick Action Cards Grid - Improved mobile responsiveness */}
          <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-5">
            {quickActions.map((action, index) => {
              const IconComponent = action.icon
              return (
                <button
                  key={index}
                  onClick={() => handleQuickAction(action.category)}
                  className={`${action.bgColor} p-3 sm:p-4 md:p-5 rounded-xl border-2 border-transparent hover:border-current transition-all duration-200 text-left group transform hover:-translate-y-1 hover:shadow-lg`}
                >
                  <div className={`${action.color} mb-2 sm:mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-200`}>
                    <IconComponent className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" />
                  </div>
                  <h3 className={`font-semibold text-xs sm:text-sm md:text-base ${action.color} mb-1 sm:mb-2 group-hover:text-opacity-80`}>
                    {t(action.titleKey as any)}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3 md:mb-4 leading-relaxed">
                    {t(action.descKey as any)}
                  </p>
                  <div className={`flex items-center text-xs font-medium ${action.color} group-hover:text-opacity-80`}>
                    <span>Report Now</span>
                    <ChevronRightIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 ml-1 group-hover:translate-x-1 transition-transform duration-200" />
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Features Section - Improved mobile responsiveness */}
      <div className="bg-gray-50 py-8 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
              How It Works
            </h2>
            <p className="text-base sm:text-lg text-gray-600 px-2">
              Simple steps to report and track civic issues
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            <div className="text-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <span className="text-base sm:text-xl md:text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">Report Issue</h3>
              <p className="text-xs sm:text-sm md:text-base text-gray-600 leading-relaxed px-1">
                Take a photo, add location, and describe the civic issue you want to report
              </p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <span className="text-base sm:text-xl md:text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">Track Progress</h3>
              <p className="text-xs sm:text-sm md:text-base text-gray-600 leading-relaxed px-1">
                Monitor the status of your complaint and receive updates from officials
              </p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <span className="text-base sm:text-xl md:text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">Get Resolution</h3>
              <p className="text-xs sm:text-sm md:text-base text-gray-600 leading-relaxed px-1">
                Officials work to resolve the issue and update you on completion
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HeroSection